import { useMemo, useState } from 'react';
import type { CalendarException } from '@chronos/shared';
import { useCalendar, useSettings } from '@/hooks/queries';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner, StatTile } from '@/components/ui';

/**
 * The company calendar, readable by everyone. Two views of the same data: a
 * list to scan and plan against, and twelve mini-months to see the shape of
 * the year. Working weekends share the dataset and are shown alongside —
 * a Saturday you are expected to work matters as much as a day off.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Today in the organisation's attendance timezone, not the browser's. */
function todayKeyIn(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

const weekdayOf = (key: string) => new Date(`${key}T00:00:00.000Z`).getUTCDay();

function longDate(key: string): string {
  return new Date(`${key}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

export function HolidaysPage() {
  // Full settings rather than the narrow /me payload: any signed-in user may
  // read them, and the calendar needs the org's timezone and weekend days.
  const { data: settings } = useSettings();
  const tz = settings?.attendanceTimezone ?? 'UTC';
  const today = todayKeyIn(tz);

  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const { data, isLoading, error } = useCalendar(year);

  const holidays = useMemo(() => (data ?? []).filter((e) => e.kind === 'HOLIDAY'), [data]);
  const workingDays = useMemo(() => (data ?? []).filter((e) => e.kind === 'WORKING'), [data]);
  const byDate = useMemo(() => new Map((data ?? []).map((e) => [e.date, e])), [data]);

  const upcoming = holidays.filter((h) => h.date >= today);
  const next = upcoming[0];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Holidays"
        description="The company calendar. Holidays are days off; working weekends are days you are expected to work."
        actions={
          <div className="flex items-center gap-2">
            <button className="btn-secondary px-2 py-1 text-sm" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">←</button>
            <span className="min-w-[3.5rem] text-center text-sm font-semibold tabular-nums text-slate-800">{year}</span>
            <button className="btn-secondary px-2 py-1 text-sm" onClick={() => setYear((y) => y + 1)} aria-label="Next year">→</button>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label={`Holidays in ${year}`} value={String(holidays.length)} tone="brand" />
        <StatTile label="Still to come" value={String(upcoming.length)} hint={year !== Number(today.slice(0, 4)) ? `for ${year}` : undefined} tone="green" />
        <StatTile label="Next holiday" value={next ? next.name : '—'} hint={next ? longDate(next.date) : 'Nothing scheduled'} />
      </div>

      <ErrorNote error={error} />

      <div className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm" role="tablist">
        {(['list', 'calendar'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={view === v}
            className={`flex-1 rounded-md px-3 py-1.5 capitalize transition ${view === v ? 'bg-white font-medium text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            onClick={() => setView(v)}
          >
            {v === 'list' ? 'List' : 'Calendar'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card className="mt-4"><div className="flex justify-center py-10"><Spinner /></div></Card>
      ) : view === 'list' ? (
        <ListView holidays={holidays} workingDays={workingDays} today={today} year={year} />
      ) : (
        <CalendarView year={year} byDate={byDate} today={today} weekendDays={settings?.weekendDays ?? [0, 6]} />
      )}
    </div>
  );
}

function ListView({ holidays, workingDays, today, year }: { holidays: CalendarException[]; workingDays: CalendarException[]; today: string; year: number }) {
  if (holidays.length === 0 && workingDays.length === 0) {
    return (
      <Card className="mt-4">
        <EmptyState title={`No holidays set for ${year}`} description="An admin adds them in Settings → Calendar." />
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Holidays</h2>
        </div>
        {holidays.length === 0 ? (
          <EmptyState title="None set" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="th">Date</th>
                  <th className="th">Day</th>
                  <th className="th">Holiday</th>
                  <th className="th">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {holidays.map((h) => {
                  const isToday = h.date === today;
                  const past = h.date < today;
                  return (
                    <tr key={h.id} className={`hover:bg-slate-50 ${past ? 'text-slate-400' : ''}`}>
                      <td className="td whitespace-nowrap tabular-nums">
                        {new Date(`${h.date}T00:00:00.000Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })}
                      </td>
                      <td className="td whitespace-nowrap">{WEEKDAYS[weekdayOf(h.date)]}</td>
                      <td className={`td font-medium ${past ? '' : 'text-slate-800'}`}>{h.name}</td>
                      <td className="td">
                        {isToday ? (
                          <span className="badge bg-emerald-100 text-emerald-800">Today</span>
                        ) : past ? (
                          <span className="text-xs text-slate-400">Passed</span>
                        ) : (
                          <span className="badge bg-brand-100 text-brand-800">Upcoming</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {workingDays.length > 0 && (
        <Card className="mt-4">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-800">Working weekends</h2>
            <p className="text-xs text-slate-500">Weekend dates that count as normal working days.</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {workingDays.map((w) => (
              <li key={w.id} className={`flex flex-wrap items-center gap-3 px-4 py-2 text-sm ${w.date < today ? 'text-slate-400' : 'text-slate-800'}`}>
                <span className="w-28 shrink-0 tabular-nums">
                  {new Date(`${w.date}T00:00:00.000Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })}
                </span>
                <span className="w-10 shrink-0 text-slate-500">{WEEKDAYS[weekdayOf(w.date)]}</span>
                <span>{w.name}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function CalendarView({
  year, byDate, today, weekendDays,
}: { year: number; byDate: Map<string, CalendarException>; today: string; weekendDays: number[] }) {
  return (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-rose-100 ring-1 ring-rose-300" /> Holiday</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-amber-100 ring-1 ring-amber-300" /> Working weekend</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded bg-slate-100" /> Weekend</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded ring-2 ring-brand-500" /> Today</span>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MONTHS.map((label, m) => (
          <Card key={label} className="p-3">
            <h3 className="mb-2 text-sm font-semibold text-slate-800">{label}</h3>
            <MonthGrid year={year} month={m} byDate={byDate} today={today} weekendDays={weekendDays} />
          </Card>
        ))}
      </div>
    </>
  );
}

function MonthGrid({
  year, month, byDate, today, weekendDays,
}: { year: number; month: number; byDate: Map<string, CalendarException>; today: string; weekendDays: number[] }) {
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium text-slate-400">
        {WEEKDAYS.map((d) => <div key={d}>{d[0]}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (day === null) return <div key={`pad-${i}`} />;
          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const entry = byDate.get(key);
          const isWeekend = weekendDays.includes(weekdayOf(key));
          const isToday = key === today;

          const tone =
            entry?.kind === 'HOLIDAY' ? 'bg-rose-100 text-rose-900 font-semibold'
            : entry?.kind === 'WORKING' ? 'bg-amber-100 text-amber-900 font-semibold'
            : isWeekend ? 'bg-slate-100 text-slate-400'
            : 'text-slate-700';

          return (
            <div
              key={key}
              title={entry ? `${entry.name} — ${longDate(key)}` : longDate(key)}
              className={`aspect-square rounded text-center text-[11px] leading-[1.9rem] tabular-nums ${tone} ${isToday ? 'ring-2 ring-brand-500' : ''}`}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}
